import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrutalHeader from './BrutalHeader';
import './App.css';

function App() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hiwRef = useRef<HTMLElement>(null);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let ctx: CanvasRenderingContext2D | null;
    let W: number, H: number, dpr: number;
    let raf: number;

    function resize() {
      if (!canvas) return;
      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      W = canvas.width;
      H = canvas.height;
      ctx = canvas.getContext('2d', { alpha: true });
    }

    const CYCLE = 12;
    function easeInOut(t: number) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    const N = 3;
    const COUNT = N * N;

    function seededRand(seed: number) {
      let s = seed;
      return () => { s = (s * 16807 + 0) % 2147483647; return s / 2147483647; };
    }
    const rand = seededRand(42);

    const homes: { x: number; y: number }[] = [];
    for (let row = 0; row < N; row++) {
      for (let col = 0; col < N; col++) {
        const bx = 0.08 + (col / (N - 1)) * 0.84;
        const by = 0.08 + (row / (N - 1)) * 0.84;
        homes.push({ x: bx + (rand() - 0.5) * 0.05, y: by + (rand() - 0.5) * 0.05 });
      }
    }

    const rotMap: number[] = [];
    for (let row = 0; row < N; row++) {
      for (let col = 0; col < N; col++) {
        rotMap.push(col * N + (N - 1 - row));
      }
    }

    const wobbles: { fx: number; fy: number; px: number; py: number }[] = [];
    for (let i = 0; i < COUNT; i++) {
      wobbles.push({ fx: 0.8 + rand() * 0.8, fy: 0.8 + rand() * 0.8, px: rand() * Math.PI * 2, py: rand() * Math.PI * 2 });
    }
    const WOBBLE_AMP = 0.012;

    function hover(homeX: number, homeY: number, wob: typeof wobbles[0], t: number) {
      return { x: homeX + Math.sin(t * wob.fx + wob.px) * WOBBLE_AMP, y: homeY + Math.cos(t * wob.fy + wob.py) * WOBBLE_AMP };
    }

    function lerpColor(a: [number, number, number], b: [number, number, number], t: number): string {
      return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)})`;
    }

    const WHITE: [number, number, number] = [244, 244, 242];
    const GREEN: [number, number, number] = [182, 255, 0];

    function loop(ts: number) {
      if (!ctx) { raf = requestAnimationFrame(loop); return; }
      ctx.clearRect(0, 0, W, H);
      const t = ts * 0.001;
      const cycle = t % CYCLE;
      const SIZE = Math.min(W, H) * 0.045;
      const cx = W / 2, cy = H / 2;
      const halfGrid = (N * SIZE) / 2;
      const form: { x: number; y: number }[] = [];
      for (let row = 0; row < N; row++) {
        for (let col = 0; col < N; col++) {
          form.push({ x: cx - halfGrid + col * SIZE + SIZE / 2, y: cy - halfGrid + row * SIZE + SIZE / 2 });
        }
      }
      const rotated = form.map((_, i) => form[rotMap[i]]);
      const homePixel = (hx: number, hy: number) => ({ x: hx * W, y: hy * H });
      const FULL = SIZE * N;

      if (cycle >= 3.5 && cycle < 8.5) {
        let color = lerpColor(WHITE, WHITE, 0);
        let angle = 0;
        if (cycle >= 4 && cycle < 5.5) { angle = easeInOut((cycle - 4) / 1.5) * (Math.PI / 2); }
        else if (cycle >= 5.5 && cycle < 6) { angle = Math.PI / 2; color = lerpColor(WHITE, GREEN, (cycle - 5.5) / 0.5); }
        else if (cycle >= 6 && cycle < 7.5) { angle = Math.PI / 2; color = lerpColor(GREEN, GREEN, 0); }
        else if (cycle >= 7.5 && cycle < 8) { angle = Math.PI / 2; color = lerpColor(GREEN, WHITE, (cycle - 7.5) / 0.5); }
        else if (cycle >= 8) { angle = Math.PI / 2; }
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(angle);
        ctx.fillStyle = color; ctx.fillRect(-FULL / 2, -FULL / 2, FULL, FULL);
        ctx.restore();
      } else {
        for (let i = 0; i < COUNT; i++) {
          let ppx: number, ppy: number;
          const home = homes[i], fi = form[i], ri = rotated[i], wob = wobbles[i];
          const destHome = homes[rotMap[i]], destWob = wobbles[rotMap[i]];
          if (cycle < 2) {
            const h = hover(home.x, home.y, wob, t); const hp = homePixel(h.x, h.y); ppx = hp.x; ppy = hp.y;
          } else if (cycle < 3.5) {
            const p = easeInOut((cycle - 2) / 1.5); const h = hover(home.x, home.y, wob, t); const hp = homePixel(h.x, h.y);
            ppx = hp.x + (fi.x - hp.x) * p; ppy = hp.y + (fi.y - hp.y) * p;
          } else if (cycle < 10) {
            const p = easeInOut((cycle - 8.5) / 1.5); const h = hover(destHome.x, destHome.y, destWob, t); const hp = homePixel(h.x, h.y);
            ppx = ri.x + (hp.x - ri.x) * p; ppy = ri.y + (hp.y - ri.y) * p;
          } else {
            const h = hover(destHome.x, destHome.y, destWob, t); const hp = homePixel(h.x, h.y); ppx = hp.x; ppy = hp.y;
          }
          ctx.fillStyle = lerpColor(WHITE, WHITE, 0);
          ctx.fillRect(ppx - SIZE / 2, ppy - SIZE / 2, SIZE, SIZE);
        }
      }
      raf = requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);
    resize();
    raf = requestAnimationFrame(loop);
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, []);

  // How It Works intersection observer
  useEffect(() => {
    const el = hiwRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-shell">
      <div className="landing-hero">
        <BrutalHeader />
        <div className="landing">
          <div className="hero-left">
            <span className="hero-eyebrow">Data Monetization Platform</span>
            <h1 className="hero-title">
              Unlock your<br /><span className="hero-highlight">data's value</span>
            </h1>
            <p className="hero-subtitle">
              We assess and prepare your business data for AI companies — at no cost to you.
              When it's purchased, you get paid.
            </p>
            <div className="hero-ctas">
              <button className="hero-cta-primary" onClick={() => navigate('/signup')}>
                Get started <span className="cta-arrow">→</span>
              </button>
              <a href="#how-it-works" className="hero-cta-secondary">How it works</a>
            </div>
          </div>
          <div className="hero-right">
            <div className="scope-canvas-wrap">
              <canvas ref={canvasRef} className="scope-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>

      {/* Proof */}
      <section className="landing-black">
        <div className="proof-container">
          <div className="proof-grid">
            <div className="proof-left">
              <h2 className="proof-heading">Leverage what you already have.</h2>
              <p className="proof-desc">
              Your operations produce more than output — they produce an archive of valuable data. Every decision you make about how you operate is invaluable to the companies building what comes next. Our team of experts helps you get paid for it.
              </p>
            </div>
            <div className="proof-right">
              <div className="proof-values">
              <div className="proof-value-item">
                  <div className="proof-value-title">Expert-level service</div>
                  <p className="proof-value-desc">
                  Our experienced team knows how to refine and optimize your data for the AI market, ensuring you get the best possible price.</p>
                </div>
                <div className="proof-value-item">
                  <div className="proof-value-title">Privacy-first handling</div>
                  <p className="proof-value-desc">All data is de-identified and anonymized before delivery, ensuring full regulatory compliance.</p>
                </div>
                <div className="proof-value-item">
                  <div className="proof-value-title">No cost</div>
                  <p className="proof-value-desc">We won't reach out until we have a match. Once we do, we'll handle extraction, preparation, and delivery.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — animated */}
      <section ref={hiwRef} className="hiw-section" id="how-it-works">
        <div className="hiw-inner">
          <div className="hiw-header">
            <span className="hiw-label">Our Process</span>
            <h2 className="hiw-title">How It Works</h2>
          </div>

          {/* Animated track */}
          <div className="hiw-track-wrap">
            <div className="hiw-track-line" />
            <div className="hiw-track-ball" />
            <div className="hiw-nodes-row">
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-1" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-2" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-3" /></div>
              <div className="hiw-node-cell"><div className="hiw-node hiw-node-4" /></div>
            </div>
          </div>

          {/* Cards */}
          <div className="hiw-cards">
            <div className="hiw-card hiw-card-1">
              <img src="/step_door.svg" alt="" className="hiw-card-icon" />
              <span className="hiw-card-num">01</span>
              <h3 className="hiw-card-title">Join Our Network</h3>
              <p className="hiw-card-desc">
                Fill out <a href="/signup" onClick={(e) => { e.preventDefault(); navigate('/signup'); }} className="prim-inline-link">our quick form</a> to join our network. It only takes a couple of minutes.
              </p>
            </div>
            <div className="hiw-card hiw-card-2">
              <img src="/step_onboard.svg" alt="" className="hiw-card-icon" />
              <span className="hiw-card-num">02</span>
              <h3 className="hiw-card-title">Onboard</h3>
              <p className="hiw-card-desc">
                If there's a match, we'll work with you to better understand your business and your data. You'll receive a quote.
              </p>
            </div>
            <div className="hiw-card hiw-card-3">
              <img src="/step_extract.svg" alt="" className="hiw-card-icon" />
              <span className="hiw-card-num">03</span>
              <h3 className="hiw-card-title">Data Preparation</h3>
              <p className="hiw-card-desc">
                We handle the collection and preparation of your data, with minimal disruption to your operations.
              </p>
            </div>
            <div className="hiw-card hiw-card-4">
              <img src="/step_paid.svg" alt="" className="hiw-card-icon" />
              <span className="hiw-card-num">04</span>
              <h3 className="hiw-card-title">Earn Revenue</h3>
              <p className="hiw-card-desc">
                You retain ownership rights for your data and get paid when it's licensed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership / AI Acceleration */}
      <section className="partner-section">
        <div className="partner-inner">
          <div className="partner-top">
            <span className="partner-eyebrow">The Partnership Advantage</span>
            <h2 className="partner-heading">We help you grow with AI,<br />not just sell to it.</h2>
            <p className="partner-subtext">
            As part of our network, you'll stay up to date on the latest AI advancements. As part of every deal, we'll deliver a full analysis of your tech stack along with concrete steps to improve productivity.
            </p>
          </div>
          <div className="partner-grid">
            <div className="partner-card">
              <span className="partner-card-num">01</span>
              <h3 className="partner-card-title">Early Access</h3>
              {/* <p className="partner-card-desc">Earn money by testing the latest AI models before they launch. Your real-world feedback helps shape the next generation of AI — and you get paid for it.</p> */}
              <p className="partner-card-desc">As part of our network, you may receive opportunities to test the latest AI models and features before they launch.</p>
            </div>
            <div className="partner-card">
              <span className="partner-card-num">02</span>
              <h3 className="partner-card-title">AI Integration</h3>
              <p className="partner-card-desc">As part of the collection process, we'll work with you to modernize your tech stack and integrate AI tools that accelerate your workflow.</p>
            </div>
            <div className="partner-card">
              <span className="partner-card-num">03</span>
              <h3 className="partner-card-title">Ongoing Partnership</h3>
              <p className="partner-card-desc">The AI landscape moves quickly, and we listen closely. We update our network with critical opportunities to stay ahead of the curve.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <IndustriesSection />

      {/* CTA */}
      <section className="landing-black cta-section">
        <div className="cta-container">
          <p className="cta-label">Ready to start?</p>
          <h2 className="cta-heading">Ready to monetize<br />your data?</h2>
          <p className="cta-subtext">Join our network of partner businesses and start creating value from data you already own.</p>
          <a href="/signup" className="cta-button" onClick={(e) => { e.preventDefault(); navigate('/signup'); }}>
            Get in touch <span className="cta-arrow">&rarr;</span>
          </a>
        </div>
      </section>
    </div>
  );
}

const INDUSTRY_TABS = [
  {
    label: 'Law',
    href: '/industries/law-firms',
    img: '/law-firm-hero.jpg',
    alt: 'Law firm attorneys',
    title: 'Monetize Your',
    dim: 'Legal Data',
    subtitle: 'Law firms generate vast amounts of structured data — case outcomes, contracts, filings, and more. Efficient helps you license this data to AI companies, creating a new revenue stream with no disruption to your practice.',
  },
  {
    label: 'Healthcare',
    href: '/industries/healthcare',
    img: '/healthcare-hero.webp',
    alt: 'Healthcare office',
    title: 'Monetize Your',
    dim: 'Clinical Data',
    subtitle: 'Healthcare providers generate enormous volumes of structured, high-value data — clinical notes, billing records, outcomes, and more. Efficient helps you license de-identified datasets to AI companies, creating a compliant new revenue stream.',
  },
  {
    label: 'Real Estate',
    href: '/industries/real-estate',
    img: '/real-estate-hero.webp',
    alt: 'Real estate office',
    title: 'Monetize Your',
    dim: 'Property Data',
    subtitle: 'Real estate firms hold rich datasets — property records, transaction histories, market analyses, and lease data. Efficient helps you license this data to AI companies building the next generation of property intelligence tools.',
  },
  {
    label: 'Hospitality',
    href: '/industries/restaurants',
    img: '/restaurant-sign.jpg',
    alt: 'Restaurant open sign',
    title: 'Monetize Your',
    dim: 'Hospitality Data',
    subtitle: 'Hotels, restaurants, and venues generate high-frequency data — bookings, guest preferences, pricing patterns, and operations logs. Efficient helps you license this data to AI companies building the next generation of travel, logistics, and consumer tools.',
  },
  {
    label: 'More',
    href: '/industries/other',
    img: '/other-hero.jpg',
    alt: 'Diverse business professionals',
    title: 'Monetize Your',
    dim: 'Business Data',
    subtitle: 'Don\'t see your industry listed? Reach out anyway — we work across verticals and some of our best partnerships have come from unexpected places.',
  },
];

function IndustriesSection() {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const ind = INDUSTRY_TABS[active];

  return (
    <section id="industries" className="industries-section">
      <div className="industries-inner">
        <div className="industry-hero-inner">
          <div className="industry-hero-left">
            <span className="industries-label">Who We Work With</span>
            <h2 className="industry-hero-title">
              {ind.title}<br />
              <span className="industry-hero-dim">{ind.dim}</span>
            </h2>
            <p className="industry-hero-subtitle">{ind.subtitle}</p>

            <div className="industry-tabs">
              {INDUSTRY_TABS.map((tab, i) => (
                <button
                  key={tab.label}
                  className={`industry-tab${i === active ? ' industry-tab-active' : ''}`}
                  onClick={() => setActive(i)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <button className="industry-cta-btn" onClick={() => navigate('/signup')}>
              Join our network →
            </button>
          </div>

          <div className="industry-hero-right">
            <img src={ind.img} alt={ind.alt} className="industry-hero-img" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default App;
